import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import axios from "axios";


/* ============================
   📦 GET ALL VENDORS (ADMIN)
   /api/admin/vendors
============================ */
export const fetchVendors = createAsyncThunk(
    "vendor/fetchVendors",
    async ({ page = 1, limit = 10, search = "" }, { rejectWithValue }) => {
        try {
            const res = await api.get(
                `/vendors/all?page=${page}&limit=${limit}&search=${search}`,
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.error || "Failed to fetch vendors"
            );
        }
    }
);

/* ============================
   📦 GET SINGLE VENDOR
   /api/admin/vendors/:id
============================ */

export const fetchVendorById = createAsyncThunk(
    "vendor/fetchVendorById",
    async (id, { rejectWithValue }) => {
        try {
            const res = await api.get(`/vendors/single-vendor/${id}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.error || "Failed to fetch vendor"
            );
        }
    }
);

/* ============================
   ✏️ UPDATE VENDOR (WITH IMAGE)
   /api/admin/vendors/:id
============================ */
export const updateVendor = createAsyncThunk(
    "vendor/updateVendor",
    async ({ vendorId, formData }, { rejectWithValue }) => {
        try {
            console.log("🟡 UPDATE VENDOR ID:", vendorId);

            // 🔍 LOG FORM DATA
            for (let pair of formData._parts) {
                console.log("🟢 FORM DATA:", pair[0], pair[1]);
            }

            const url = `/vendors/update-vendor/${vendorId}`;
            console.log("🟡 HIT URL:", url);

            const res = await api.put(url, formData,);

            console.log("🟢 RESPONSE:", res);
            console.log("🟢 RESPONSE.DATA:", res.data);

            return res.data;
        } catch (err) {
            console.log("🔴 AXIOS ERROR:", err);

            console.log(
                "🔴 ERROR RESPONSE:",
                err.response?.data
            );

            console.log(
                "🔴 ERROR STATUS:",
                err.response?.status
            );

            return rejectWithValue(
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Vendor update failed"
            );
        }
    }
);


/* ============================
   ✅ APPROVE VENDOR
   /api/admin/vendors/:id/approve
============================ */
export const approveVendor = createAsyncThunk(
    "vendor/approveVendor",
    async (vendorId, { rejectWithValue }) => {
        try {
            const res = await api.put(`/admin/vendors/${vendorId}/approve`);
            return res.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.error || "Vendor approval failed"
            );
        }
    }
);


/* ============================
   GET Vendor BY CATEGORY
============================ */
export const getVendorByCategory = createAsyncThunk(
    "vendor/getByCategory",
    async ({ categoryId, page = 1, limit = 10, city }, { rejectWithValue }) => {
        try {
            const res = await api.get(
                `/vendors/categorywisevendor/${categoryId}?page=${page}&limit=${limit}&city=${city}`
            );

            if (!res.data.success) {
                return rejectWithValue(res.data.message);
            }

            return res.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message ||
                "Failed to fetch vendors by category"
            );
        }
    }
);

/* ============================
   🧠 VENDOR SLICE
============================ */
const vendorSlice = createSlice({
    name: "vendor",
    initialState: {
        vendors: [],
        vendor: null,

        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 1,
        },

        categoryVendor: [],         // 👈 CATEGORY WISE
        categoryPagination: null,

        loading: false,
        error: null,
        successMessage: null,
    },

    reducers: {
        clearVendorState: (state) => {
            state.error = null;
            state.successMessage = null;
        },
        clearCategoryVendor: (state) => {
            state.categoryVendor = [];
            state.categoryPagination = null;
        }
    },

    extraReducers: (builder) => {
        builder

            /* ===== GET ALL VENDORS ===== */
            .addCase(fetchVendors.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchVendors.fulfilled, (state, action) => {
                state.loading = false;
                state.vendors = action.payload.vendors;
                state.pagination = {
                    total: action.payload.total,
                    page: action.payload.page,
                    limit: action.payload.limit,
                    totalPages: action.payload.totalPages,
                };
            })
            .addCase(fetchVendors.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== GET SINGLE VENDOR ===== */
            .addCase(fetchVendorById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchVendorById.fulfilled, (state, action) => {
                state.loading = false;
                state.vendor = action.payload;
            })
            .addCase(fetchVendorById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== UPDATE VENDOR ===== */
            .addCase(updateVendor.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateVendor.fulfilled, (state, action) => {
                state.loading = false;
                state.vendor = action.payload.vendor;
                state.successMessage = action.payload.message;
            })
            .addCase(updateVendor.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== GET VENDOR BY CATEGORY ===== */
            .addCase(getVendorByCategory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })

            .addCase(getVendorByCategory.fulfilled, (state, action) => {
                state.loading = false;

                const { data, pagination } = action.payload;

                state.categoryVendor =
                    pagination.page === 1
                        ? data
                        : [...state.categoryVendor, ...data];

                state.categoryPagination = pagination;
            })

            .addCase(getVendorByCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== APPROVE VENDOR ===== */
            .addCase(approveVendor.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(approveVendor.fulfilled, (state, action) => {
                state.loading = false;
                state.vendor = action.payload.vendor;
                state.successMessage = action.payload.message;

                // list update (optional)
                state.vendors = state.vendors.map((v) =>
                    v._id === action.payload.vendor._id
                        ? action.payload.vendor
                        : v
                );
            })
            .addCase(approveVendor.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearVendorState, clearCategoryVendor } = vendorSlice.actions;
export default vendorSlice.reducer;
